import React from 'react';

export interface PageHeaderProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  title: React.ReactNode;
  context?: React.ReactNode;
  description?: React.ReactNode;
  metadata?: React.ReactNode;
  status?: React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryActions?: React.ReactNode;
  sticky?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, context, description, metadata, status, primaryAction, secondaryActions, sticky = false, className = '', ...props }) => (
  <header className={`${sticky ? 'sticky top-0 z-10 bg-dark-900/95' : ''} border-b border-slate-800 pb-5 sm:pb-6 ${className}`} {...props}>
    {context && <div className="mb-3 text-sm text-slate-400">{context}</div>}
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3"><h1 className="text-2xl font-bold text-white">{title}</h1>{status}</div>
        {description && <div className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">{description}</div>}
        {metadata && <div className="mt-3 text-sm text-slate-400 tabular-nums">{metadata}</div>}
      </div>
      {(primaryAction || secondaryActions) && <div className="flex shrink-0 flex-wrap items-center gap-2">{secondaryActions}{primaryAction}</div>}
    </div>
  </header>
);

type TemplateElement = 'div' | 'section' | 'article' | 'main';
export type PageTemplateKind = 'overview' | 'directory' | 'detail' | 'master-detail' | 'form-workflow' | 'system-state';
export interface TemplateProps extends React.HTMLAttributes<HTMLElement> { header?: React.ReactNode; children: React.ReactNode; as?: TemplateElement; }
const Template: React.FC<TemplateProps & { kind: string }> = ({ kind, header, children, as: Element = 'div', className = '', ...props }) => (
  <Element data-page-template={kind} className={`space-y-6 sm:space-y-8 ${className}`} {...props}>{header}{children}</Element>
);
export const OverviewTemplate: React.FC<TemplateProps> = (props) => <Template kind="overview" {...props} />;
export const DirectoryTemplate: React.FC<TemplateProps> = (props) => <Template kind="directory" {...props} />;
export const DetailTemplate: React.FC<TemplateProps> = (props) => <Template kind="detail" {...props} />;
export const FormWorkflowTemplate: React.FC<TemplateProps> = (props) => <Template kind="form-workflow" {...props} />;
export const SystemStateTemplate: React.FC<TemplateProps> = ({ as = 'section', ...props }) => <Template kind="system-state" as={as} {...props} />;

export interface PageTemplateStateProps extends TemplateProps { template: PageTemplateKind; }
export const PageTemplateState: React.FC<PageTemplateStateProps> = ({ template, ...props }) => (
  <Template kind={template} data-page-template-state="true" {...props} />
);

export interface MasterDetailTemplateProps extends Omit<TemplateProps, 'children'> { master: React.ReactNode; detail: React.ReactNode; masterLabel: string; detailLabel: string; }
export const MasterDetailTemplate: React.FC<MasterDetailTemplateProps> = ({ master, detail, masterLabel, detailLabel, header, className = '', ...props }) => (
  <Template kind="master-detail" header={header} className={className} {...props}>
    <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)]">
      <section aria-label={masterLabel} className="min-w-0">{master}</section>
      <section aria-label={detailLabel} className="min-w-0">{detail}</section>
    </div>
  </Template>
);

export interface SystemStateLayoutProps extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> { title: React.ReactNode; description?: React.ReactNode; actions?: React.ReactNode; }
export const SystemStateLayout: React.FC<SystemStateLayoutProps> = ({ title, description, actions, children, className = '', ...props }) => (
  <main className={`flex min-h-screen items-center justify-center bg-dark-900 px-4 py-12 text-white ${className}`} {...props}>
    <section className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-800/30 p-6 sm:p-8" aria-labelledby="system-state-title">
      <h1 id="system-state-title" className="text-2xl font-bold">{title}</h1>
      {description && <div className="mt-3 text-sm leading-6 text-slate-400">{description}</div>}
      {children && <div className="mt-6">{children}</div>}
      {actions && <div className="mt-6 flex flex-wrap gap-2">{actions}</div>}
    </section>
  </main>
);
